/**
 * The board itself: one `<canvas>`, an infinite plane behind it.
 *
 * ── One transform, one element store ────────────────────────────────────────
 * Elements live in world units and the `View` is the window onto them, so pan
 * and zoom are two numbers and a scale rather than a property of every element.
 * Pointer positions are turned into world units on the way in; nothing past
 * that point knows the viewport exists.
 *
 * ── The element set belongs to the client, not to this component ────────────
 * `elements` comes in as a prop and every change goes out through `apply` as a
 * `BoardOp`. A draft — the shape under the pointer right now — is local and is
 * committed as one op when the pointer lifts, so a drag is one frame on the
 * wire rather than sixty.
 *
 * ── Everybody else, as they draw ────────────────────────────────────────────
 * The draft also goes out through `onLive`, under the id it will be committed
 * with, and the pointer through `onCursor`; the client coalesces both and the
 * server stores neither. Other people's live elements are drawn under the
 * draft and never hit-tested, and their cursors are a DOM layer above the
 * canvas, named and coloured per person.
 *
 * ── Undo is local, by inversion ─────────────────────────────────────────────
 * `BoardDO` keeps the elements and not the ops that made them, so there is no
 * history to walk back on the server. Each committed change records the ops
 * that reverse it, against the elements as they were. Undoing somebody else's
 * stroke is not a thing that exists.
 *
 * ── The style controls reach the selection ──────────────────────────────────
 * `update` has been a valid op since the contract landed and nothing here had
 * ever sent one with a style in it, so an element's look was fixed at birth:
 * the only way to recolour a rectangle was to delete it and draw it again.
 * Changing a colour, a width or a fill while something is selected now patches
 * it, exactly as the desktop's `applyStyleToSelection` does — and text can be
 * opened again with a double click, because `patch.text` was the other field
 * nothing had ever sent and a typo was permanent until the element went.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { OWNS_SPACE } from '../call/keys';
import type { PeerView } from '../../lib/board/peers';
import type { BoardElement, BoardOp } from '../../lib/board/types';
import {
  fitView, pickElement, simplify, toScreen, toWorld, zoomAround, type View
} from './geometry';
import { TEXT_LINE, TEXT_SIZE, TEXT_FONT, draw } from './render';
import { DEFAULT_WIDTH, SWATCHES, Toolbar, type Tool } from './Toolbar';

interface Size {
  w: number;
  h: number;
}

/** What the toolbar holds, and what a new element is born with. `fill` is `''`
 *  for no fill — an outline, which is the default and what the hit test reads
 *  as "near the line" rather than "anywhere inside". */
interface Style {
  color: string;
  width: number;
  fill: string;
}

/** A change, and the ops that put it back. `clear` needs an array both ways,
 *  so both halves are arrays. */
interface Change {
  forward: BoardOp[];
  back: BoardOp[];
}

interface Gesture {
  mode: 'pan' | 'draw' | 'move';
  startX: number;
  startY: number;
  view: View;
  /** `move` only: the element as it was, and where the pointer took hold. */
  original?: BoardElement;
}

interface Pinch {
  distance: number;
  midX: number;
  midY: number;
  view: View;
}

/** The ops that undo one op, against the elements as they are now. */
export function inverseOps(elements: BoardElement[], op: BoardOp): BoardOp[] {
  switch (op.k) {
    case 'add':
      return [{ k: 'remove', id: op.el.id }];
    case 'remove': {
      const el = elements.find((e) => e.id === op.id);
      return el ? [{ k: 'add', el }] : [];
    }
    case 'update': {
      const el = elements.find((e) => e.id === op.id);
      if (!el) return [];
      const patch: Partial<BoardElement> = {};
      for (const key of Object.keys(op.patch) as (keyof BoardElement)[]) {
        // Assigning through a union of value types is what `Partial` cannot
        // express here; the key and the value come from the same element.
        (patch as Record<string, unknown>)[key] = el[key];
      }
      return [{ k: 'update', id: op.id, patch }];
    }
    case 'clear':
      return elements.map((el) => ({ k: 'add', el }));
  }
}

export interface CanvasProps {
  elements: BoardElement[];
  apply: (op: BoardOp) => void;
  /** Used for the exported file's name. */
  name: string;
  /** Other people's cursors and the elements they are still drawing. */
  others?: PeerView;
  /** This pointer, in world units, whenever it moves over the canvas. */
  onCursor?: (x: number, y: number) => void;
  /** The element being drawn, under the id it will be added with; `null` when
   *  the gesture ends. */
  onLive?: (el: BoardElement | null) => void;
}

const NOBODY: PeerView = { cursors: [], live: [], here: [] };

export function Canvas({ elements, apply, name, others = NOBODY, onCursor, onLive }: CanvasProps) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  const [size, setSize] = useState<Size>({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ x: 0, y: 0, scale: 1 });
  const [tool, setTool] = useState<Tool>('select');
  const [color, setColor] = useState(SWATCHES[0]!.value);
  const [fill, setFill] = useState('');
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BoardElement | null>(null);
  /** `id` is the element being corrected; absent is a new one. */
  const [editing, setEditing] = useState<{
    x: number;
    y: number;
    value: string;
    id?: string;
  } | null>(null);
  const [paint, setPaint] = useState(0);
  const [history, setHistory] = useState<{ undo: Change[]; redo: Change[] }>({
    undo: [],
    redo: []
  });

  // Handlers run outside React's render, so they read the current values from
  // refs rather than the closure they were made in.
  const viewRef = useRef(view);
  viewRef.current = view;
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const toolRef = useRef(tool);
  toolRef.current = tool;
  const styleRef = useRef<Style>({ color, width, fill });
  styleRef.current = { color, width, fill };

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);
  const pinch = useRef<Pinch | null>(null);
  const points = useRef<number[]>([]);
  const spaceDown = useRef(false);
  /** The id the element being drawn will be added under, minted when the
   *  pointer goes down so the live copy others see is replaced by the real one. */
  const drawingId = useRef('');

  /** Apply and remember how to put it back. */
  const commit = useCallback(
    (ops: BoardOp[]) => {
      if (ops.length === 0) return;
      let current = elementsRef.current;
      const back: BoardOp[] = [];
      for (const op of ops) {
        back.unshift(...inverseOps(current, op));
        current = applyLocal(current, op);
        apply(op);
      }
      setHistory((h) => ({ undo: [...h.undo, { forward: ops, back }], redo: [] }));
    },
    [apply]
  );

  /**
   * The style controls, applied to whatever is selected.
   *
   * Only the fields that actually differ go in the patch: every field absent is
   * an op the server accepts and applies to nothing. A stroke has no inside and
   * a text element's fill would paint a block behind the words, so neither
   * takes one — which is the desktop's rule, not a second opinion held here.
   */
  /**
   * Re-style the selection — **only the control that was touched.**
   *
   * The toolbar describes the *next* element, and nothing loads a selected one
   * back into it. So pushing the whole style would repaint a selected red line
   * violet because somebody typed a new width into the box beside the swatches.
   * The desktop's `applyStyleToSelection` takes the same one field, for the
   * same reason.
   */
  const restyle = (field: 'color' | 'fill' | 'width', value: string | number) => {
    if (!selectedId) return;
    const el = elementsRef.current.find((e) => e.id === selectedId);
    if (!el) return;
    if (field === 'fill' && el.type !== 'rect' && el.type !== 'ellipse') return;

    const current = field === 'fill' ? (el.fill ?? '') : el[field];
    if (current === value) return;

    commit([{ k: 'update', id: selectedId, patch: { [field]: value } }]);
  };

  // The ops go out here rather than inside the updater: a state updater runs
  // twice under StrictMode, and an undo that sent every frame twice would be a
  // board that repaired itself on a dev build and not on a real one.
  const undo = useCallback(() => {
    const change = history.undo[history.undo.length - 1];
    if (!change) return;
    for (const op of change.back) apply(op);
    setHistory({ undo: history.undo.slice(0, -1), redo: [...history.redo, change] });
  }, [apply, history]);

  const redo = useCallback(() => {
    const change = history.redo[history.redo.length - 1];
    if (!change) return;
    for (const op of change.forward) apply(op);
    setHistory({ undo: [...history.undo, change], redo: history.redo.slice(0, -1) });
  }, [apply, history]);

  // ── Size, and the paint ───────────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    });
    observer.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => observer.disconnect();
  }, []);

  // The colours are CSS variables read at draw time, so a theme change has to
  // reach the canvas the same way a resize does.
  useEffect(() => {
    const observer = new MutationObserver(() => setPaint((n) => n + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0 || size.h === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size.w * dpr);
    canvas.height = Math.round(size.h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const style = getComputedStyle(canvas);
    draw(ctx, [...elements, ...others.live, ...(draft ? [draft] : [])], {
      view,
      width: size.w,
      height: size.h,
      background: style.getPropertyValue('--app-panel').trim() || '#f8f9fd',
      grid: style.getPropertyValue('--border-strong').trim() || '#cfd3e5',
      selectedId,
      selectionColor: style.getPropertyValue('--primary').trim() || '#796cbf'
    });
  }, [elements, others.live, draft, view, size, selectedId, paint]);

  // ── Keys ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT');
      if (typing) return;

      if (e.code === 'Space') {
        // Push to talk leaves Space alone here, so nothing else stops it
        // scrolling the page.
        if (e.target === canvasRef.current) e.preventDefault();
        spaceDown.current = true;
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        commit([{ k: 'remove', id: selectedId }]);
        setSelectedId(null);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [commit, redo, selectedId, undo]);

  // ── The pointer ───────────────────────────────────────────────────────────

  const local = (e: React.MouseEvent): { x: number; y: number } => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // Pressure is ignored: a stroke's weight is the width control, so a board
    // drawn with a finger and one drawn with a pen look the same to everyone
    // else on it.
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    if (pointers.current.size === 2) {
      if (gesture.current?.mode === 'draw') onLive?.(null);
      gesture.current = null;
      setDraft(null);
      pinch.current = startPinch(pointers.current, viewRef.current);
      return;
    }

    const world = toWorld(viewRef.current, p.x, p.y);
    const active = toolRef.current;

    if (e.button === 1 || spaceDown.current) {
      // Middle-button autoscroll otherwise takes the drag away on Windows.
      e.preventDefault();
      // Which also stops the press focusing the canvas, and the canvas holds
      // Space against push to talk only while it has focus.
      canvasRef.current?.focus({ preventScroll: true });
      gesture.current = { mode: 'pan', startX: p.x, startY: p.y, view: viewRef.current };
      return;
    }

    if (active === 'text') {
      // **The press that opens the editor must not also move focus.** A trusted
      // mousedown blurs the textarea React has just mounted, the blur commits
      // an empty box and closes it, and placing text with the mouse therefore
      // did not work at all — the editor appeared and went inside one click.
      // Preventing the default here suppresses the compatibility mousedown and
      // with it the focus change; every other press keeps its default, because
      // clicking away is how an edit finishes.
      //
      // Which is also why whatever was open is committed here by hand: with no
      // blur to do it, opening a second box would have thrown the first one's
      // words away.
      e.preventDefault();
      commitText();
      setEditing({ x: world.x, y: world.y, value: '' });
      return;
    }

    if (active === 'select') {
      const hit = pickElement(elementsRef.current, world.x, world.y, 8 / viewRef.current.scale);
      setSelectedId(hit ? hit.id : null);
      gesture.current = hit
        ? { mode: 'move', startX: world.x, startY: world.y, view: viewRef.current, original: hit }
        : { mode: 'pan', startX: p.x, startY: p.y, view: viewRef.current };
      return;
    }

    setSelectedId(null);
    gesture.current = { mode: 'draw', startX: world.x, startY: world.y, view: viewRef.current };
    points.current = [world.x, world.y];
    drawingId.current = crypto.randomUUID();
    setDraft(shapeFrom(active, world.x, world.y, world.x, world.y, styleRef.current));
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = local(e);
    const here = toWorld(viewRef.current, p.x, p.y);
    onCursor?.(here.x, here.y);
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, p);

    if (pinch.current && pointers.current.size === 2) {
      setView(movePinch(pointers.current, pinch.current));
      return;
    }

    const g = gesture.current;
    if (!g) return;
    const world = toWorld(g.view, p.x, p.y);

    if (g.mode === 'pan') {
      setView({
        x: g.view.x - (p.x - g.startX) / g.view.scale,
        y: g.view.y - (p.y - g.startY) / g.view.scale,
        scale: g.view.scale
      });
      return;
    }

    if (g.mode === 'move' && g.original) {
      const dx = world.x - g.startX;
      const dy = world.y - g.startY;
      setDraft({ ...g.original, x: g.original.x + dx, y: g.original.y + dy });
      return;
    }

    const active = toolRef.current;
    let next: BoardElement;
    if (active === 'pen') {
      points.current.push(world.x, world.y);
      next = strokeFrom(points.current, styleRef.current, 'draft');
    } else {
      next = shapeFrom(active, g.startX, g.startY, world.x, world.y, styleRef.current);
    }
    setDraft(next);
    onLive?.({ ...next, id: drawingId.current });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;

    const g = gesture.current;
    gesture.current = null;
    if (!g) return;

    if (g.mode === 'pan') return;

    if (g.mode === 'move' && g.original) {
      const moved = draft;
      setDraft(null);
      if (!moved || (moved.x === g.original.x && moved.y === g.original.y)) return;
      commit([{ k: 'update', id: g.original.id, patch: { x: moved.x, y: moved.y } }]);
      return;
    }

    const active = toolRef.current;
    setDraft(null);
    // The live copy comes down after the commit, so the finished element is
    // already on its way: the other order would blink it out for a frame.
    const finish = (ops: BoardOp[]) => {
      commit(ops);
      onLive?.(null);
    };

    if (active === 'pen') {
      // A world-unit tolerance scaled by the zoom, so a stroke drawn zoomed out
      // is not flattened harder than the same stroke drawn zoomed in.
      const el = strokeFrom(
        simplify(points.current, 1.5 / g.view.scale),
        styleRef.current,
        drawingId.current
      );
      points.current = [];
      finish((el.points?.length ?? 0) >= 2 ? [{ k: 'add', el }] : []);
      return;
    }

    const p = local(e);
    const world = toWorld(g.view, p.x, p.y);
    const el = shapeFrom(active, g.startX, g.startY, world.x, world.y, styleRef.current);
    // A click that never moved is not a shape.
    if (Math.abs(el.w) < 2 && Math.abs(el.h) < 2) {
      finish([]);
      return;
    }
    finish([{ k: 'add', el: { ...el, id: drawingId.current } }]);
  };

  /** Double-clicking a text element opens it again, whatever the tool. */
  const onDoubleClick = (e: React.MouseEvent) => {
    const p = local(e);
    const world = toWorld(viewRef.current, p.x, p.y);
    const hit = pickElement(elementsRef.current, world.x, world.y, 8 / viewRef.current.scale);
    if (!hit || hit.type !== 'text') return;
    // The press this double click is the second half of already took a move
    // gesture on the element. It commits nothing, because nothing moved.
    gesture.current = null;
    setSelectedId(hit.id);
    setEditing({ x: hit.x, y: hit.y, value: hit.text ?? '', id: hit.id });
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const factor = Math.exp(-e.deltaY * 0.0015);
    setView((v) => zoomAround(v, e.clientX - rect.left, e.clientY - rect.top, factor));
  };

  // ── Text ──────────────────────────────────────────────────────────────────

  const commitText = () => {
    const box = editing;
    setEditing(null);
    if (!box) return;
    const text = box.value.trim();

    if (!text) {
      // Emptying one that exists removes it: a text element with nothing in it
      // is invisible and still catches every click over its box.
      if (!box.id) return;
      commit([{ k: 'remove', id: box.id }]);
      setSelectedId(null);
      return;
    }

    const lines = text.split('\n');
    const w = measureText(lines);
    const h = lines.length * TEXT_SIZE * TEXT_LINE;

    // Re-measured the way creation measures, so a corrected note's box is the
    // box its words need rather than the one the old words needed.
    if (box.id) {
      commit([{ k: 'update', id: box.id, patch: { text, w, h } }]);
      return;
    }

    const el: BoardElement = {
      id: crypto.randomUUID(),
      type: 'text',
      x: box.x,
      y: box.y,
      w,
      h,
      text,
      color: styleRef.current.color,
      width: 1
    };
    commit([{ k: 'add', el }]);
  };

  // The session, not the box: `editing` is a new object on every keystroke, and
  // an effect that ran on each of them would put the caret back at the end of
  // the line in the middle of a correction.
  const session = editing ? (editing.id ?? 'new') : null;
  useEffect(() => {
    if (!session) return;
    const field = textRef.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, [session]);

  // ── The strip's other buttons ─────────────────────────────────────────────

  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0) return;
    const dpr = window.devicePixelRatio || 1;

    const out = document.createElement('canvas');
    out.width = Math.round(size.w * dpr);
    out.height = Math.round(size.h * dpr);
    const ctx = out.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const style = getComputedStyle(canvas);
    draw(ctx, elements, {
      view,
      width: size.w,
      height: size.h,
      background: style.getPropertyValue('--app-panel').trim() || '#ffffff'
    });

    out.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name || 'board'}.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    });
  };

  const editingAt = editing
    ? {
        left: (editing.x - view.x) * view.scale,
        top: (editing.y - view.y) * view.scale
      }
    : null;

  // Correcting a note keeps the colour it was written in, rather than taking
  // whatever the toolbar happens to hold now.
  const editingInk =
    (editing?.id ? elements.find((el) => el.id === editing.id)?.color : undefined) ?? color;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Toolbar
        tool={tool}
        onTool={setTool}
        color={color}
        onColor={(next) => {
          setColor(next);
          restyle('color', next);
        }}
        fill={fill}
        onFill={(next) => {
          setFill(next);
          restyle('fill', next);
        }}
        width={width}
        onWidth={(next) => {
          setWidth(next);
          restyle('width', next);
        }}
        canUndo={history.undo.length > 0}
        canRedo={history.redo.length > 0}
        onUndo={undo}
        onRedo={redo}
        onFit={() => setView(fitView(elements, size.w, size.h))}
        onExport={exportPng}
        hasSelection={selectedId !== null}
        onDeleteSelected={() => {
          if (!selectedId) return;
          commit([{ k: 'remove', id: selectedId }]);
          setSelectedId(null);
        }}
      />

      <div
        ref={containerRef}
        style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <canvas
          ref={canvasRef}
          aria-label={t('boards.canvas')}
          // Focusable, so a press gives it focus, and with it Space: in a
          // meeting with push to talk on, holding Space here pans and never
          // opens the microphone. See components/call/keys.ts.
          tabIndex={0}
          {...{ [OWNS_SPACE]: '' }}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            touchAction: 'none',
            cursor: tool === 'select' ? 'default' : 'crosshair'
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          onWheel={onWheel}
        />

        {others.cursors.map((c) => {
          const at = toScreen(view, c.x, c.y);
          return (
            <div
              key={c.id}
              aria-hidden
              style={{
                position: 'absolute',
                left: at.x,
                top: at.y,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}
            >
              <svg width="14" height="18" viewBox="0 0 14 18" style={{ display: 'block' }}>
                <path d="M1 1 L1 15 L5 11 L8 17 L10 16 L7 10 L13 10 Z" fill={c.colour} stroke="#ffffff" strokeWidth="1.2" strokeLinejoin="round" />
              </svg>
              {c.label && (
                <span
                  style={{
                    marginTop: 12,
                    padding: '1px 6px',
                    borderRadius: 6,
                    background: c.colour,
                    color: '#ffffff',
                    fontSize: 12,
                    lineHeight: '16px',
                    whiteSpace: 'nowrap',
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {c.label}
                </span>
              )}
            </div>
          );
        })}

        {editing && editingAt && (
          <textarea
            ref={textRef}
            value={editing.value}
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onBlur={commitText}
            // Ctrl+Enter finishes and Escape throws the edit away, which is
            // what the desktop's editor does key for key. Enter has to stay a
            // newline — this is several lines, and an infinite plane has
            // nowhere to put a Done button.
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(null);
                return;
              }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                commitText();
              }
            }}
            aria-label={t('boards.tool.text')}
            style={{
              position: 'absolute',
              left: editingAt.left,
              top: editingAt.top,
              minWidth: 160,
              minHeight: TEXT_SIZE * view.scale * TEXT_LINE,
              font: TEXT_FONT,
              fontSize: TEXT_SIZE * view.scale,
              lineHeight: TEXT_LINE,
              color: editingInk,
              // A well rather than nothing: an element being corrected is still
              // drawn on the canvas under this, and two copies of the same
              // words half a pixel apart is what transparent looked like.
              background: 'var(--surface-sunken)',
              border: '1px solid var(--primary)',
              borderRadius: 8,
              outline: 'none',
              padding: 2,
              resize: 'none'
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

const applyLocal = (elements: BoardElement[], op: BoardOp): BoardElement[] => {
  switch (op.k) {
    case 'add':
      return [...elements.filter((e) => e.id !== op.el.id), op.el];
    case 'update':
      return elements.map((e) => (e.id === op.id ? { ...e, ...op.patch } : e));
    case 'remove':
      return elements.filter((e) => e.id !== op.id);
    case 'clear':
      return [];
  }
};

function startPinch(pointers: Map<number, { x: number; y: number }>, view: View): Pinch {
  const [a, b] = [...pointers.values()];
  return {
    distance: Math.max(Math.hypot(b!.x - a!.x, b!.y - a!.y), 1),
    midX: (a!.x + b!.x) / 2,
    midY: (a!.y + b!.y) / 2,
    view
  };
}

/** Two fingers pan and zoom together: the midpoint moves the world, the spread
 *  scales it about that midpoint. */
function movePinch(pointers: Map<number, { x: number; y: number }>, start: Pinch): View {
  const [a, b] = [...pointers.values()];
  const distance = Math.max(Math.hypot(b!.x - a!.x, b!.y - a!.y), 1);
  const midX = (a!.x + b!.x) / 2;
  const midY = (a!.y + b!.y) / 2;

  const zoomed = zoomAround(start.view, start.midX, start.midY, distance / start.distance);
  return {
    x: zoomed.x - (midX - start.midX) / zoomed.scale,
    y: zoomed.y - (midY - start.midY) / zoomed.scale,
    scale: zoomed.scale
  };
}

function shapeFrom(
  tool: Tool,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  style: Style
): BoardElement {
  const type = tool === 'select' || tool === 'pen' || tool === 'text' ? 'rect' : tool;
  const fillable = type === 'rect' || type === 'ellipse';
  return {
    id: 'draft',
    type,
    x: x1,
    y: y1,
    w: x2 - x1,
    h: y2 - y1,
    color: style.color,
    width: style.width,
    // A line has no inside, and the field's absence is what the hit test reads
    // as "near the line" rather than "anywhere in the box".
    ...(fillable && style.fill ? { fill: style.fill } : {})
  };
}

/** World points in, one element out — the points stored relative to the first,
 *  as `BoardElement.points` is defined. */
function strokeFrom(worldPoints: number[], style: Style, id: string): BoardElement {
  const x = worldPoints[0] ?? 0;
  const y = worldPoints[1] ?? 0;
  const points: number[] = [];
  for (let i = 0; i + 1 < worldPoints.length; i += 2) {
    points.push(worldPoints[i]! - x, worldPoints[i + 1]! - y);
  }
  let w = 0;
  let h = 0;
  for (let i = 0; i + 1 < points.length; i += 2) {
    w = Math.max(w, points[i]!);
    h = Math.max(h, points[i + 1]!);
  }
  return { id, type: 'stroke', x, y, w, h, points, color: style.color, width: style.width };
}

/** The widest line, measured with the font the canvas draws it in. */
function measureText(lines: string[]): number {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 200;
  ctx.font = TEXT_FONT;
  return Math.max(...lines.map((line) => ctx.measureText(line).width), 40);
}
